import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  openNewPage,
  getHostName,
  API_GET_ORDER_DETAILS,
} from "../modals/Constants";
import { getStorage } from "../modals/Storage";
import general from "../css/General.module.css";
import screen from "../css/Screen.module.css";
import AlertDialog from "../dialogs/AlertDialog/dialog";
import LoadingDialog from "../dialogs/LoadingDialog/dialog";
import HeaderView from "../other-components/HeaderView/view";

function MyOrderDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteID, setDeleteID] = useState("");
  const [deliveryStatusCode, setDeliveryStatusCode] = useState(0);
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [values, setConstants] = useState({
    totalOrderAmount: 0,
    productOrderID: "",
    productSize: "",
    productQuantity: 0,
    productDiscount: 0,
    productCouponDiscount: 0,
    productOrderedOn: "",
    productPaymentMethod: "",
    productList: [],
    dataLoaded: false,
  });

  const [open, showDeleteAlert] = useState(false);

  const updateUIItems = (items) => {
    const productViews = items.map((data) => (
      <div className={screen.cartItem} key={data.item_id}>
        <img
          className="w-100-p br-5"
          src={data.item_media}
          onClick={() => openNewPage(getHostName() + "/p/" + data.item_slug)}
          alt={data.item_name}
        />

        <div className={screen.cartContentsView}>
          <h6 className={screen.title}>{data.item_name}</h6>
          <p className={screen.sizeView}>Size: {data.item_size}</p>
          <h6 className={screen.pricingView}>₹{data.item_price}</h6>
        </div>
      </div>
    ));

    const totalAmount = items.reduce(
      (sum, item) => sum + parseFloat(item.item_price || 0),
      0
    );

    setConstants((prev) => ({
      ...prev,
      totalOrderAmount: totalAmount,
      productList: productViews,
    }));
  };

  const getDeliveryStatusCode = (status) => {
    let code = 0;
    if (status === "shipped") {
      code = 1;
    } else if (status === "outfordelivery") {
      code = 2;
    } else if (status === "delivered") {
      code = 3;
    }

    setDeliveryStatusCode(code)
  }

  async function loadRecords() {
    try {
      setLoading(true);

      const formData = {
        ACCOUNT_ID: getStorage("accountID"),
        ORDER_ID: query,
      };

      const res = await fetch(API_GET_ORDER_DETAILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${getStorage("accountSecret")}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      setLoading(false);

      if (data.status_code === "success") {
        let product = data.data[0];
        getDeliveryStatusCode(product.item_status);

        setConstants((previousState) => {
          return {
            ...previousState,
            productOrderID: product.item_id,
            productSize: product.item_size,
            productQuantity: product.item_quantity,
            productDiscount: product.item_discount,
            productCouponDiscount: product.item_coupon_discount,
            productOrderedOn: product.item_ordered_on,
            productPaymentMethod: product.item_payment_method,
          };
        });

        updateUIItems(data.data);
      }
    } catch (error) {
      setLoading(false);
    }
  }

  const updateProduct = (cartID, addQuantity = false, isRemove = false) => {
    if (isRemove) {
      showDeleteAlert(false);
    }

    async function requestAPI(url, formData) {
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${getStorage("accountSecret")}`,
          },
          body: JSON.stringify(formData),
        });

        const data = await res.json();
        setLoading(false);

        if (data.status_code === "success") {
          loadRecords();
        }
      } catch (error) {
        setLoading(false);
      }
    }

    if (cartID) {
      setLoading(true);
      const formData = {
        ACCOUNT_ID: getStorage("accountID"),
        CART_ID: cartID,
        CART_ADD_QUANTITY: addQuantity,
        CART_DELETE: isRemove,
      };
      requestAPI(API_GET_ORDER_DETAILS, formData);
    }
  };

  useEffect(() => {
    if (!query) return;
    if (!values.dataLoaded) {
      setConstants((prev) => ({ ...prev, dataLoaded: true }));
      loadRecords();
    }
  }, [query]);

  return (
    <div className={general.appView}>
      <LoadingDialog isOpen={loading} />

      <AlertDialog
        isOpen={open}
        title="Are you sure you want to remove this?"
        onClose={() => showDeleteAlert(false)}
        actions={[
          { label: "No", variant: "neutral", onClick: () => showDeleteAlert(false) },
          { label: "Yes, Remove", variant: "positive", onClick: () => updateProduct(deleteID, false, true) },
        ]}
      />

      <div className={general.appChildView}>
        <HeaderView screen={"myorders"} />

        <div className={general.appLocationViews}>
          <p>My order details</p>
        </div>

        <div className={general.appContentsView}>
          <div className={[screen.cartView, general.mgB100px].join(" ")}>
            <div className={screen.contentsView}>
              {values.productList}

              <div className={screen.orderTrackingView}>
                <div className={[screen.orderTrackerStep, deliveryStatusCode === 0 ? screen.halfCompleted : screen.completed].join(" ")}>
                  <div className={screen.trackPoint}></div>

                  <p>Order confirmed</p>
                  <p>Sep 22, 2025</p>
                </div>

                <div className={[screen.orderTrackerStep, deliveryStatusCode === 1 ? screen.halfCompleted : deliveryStatusCode > 1 && (screen.completed)].join(" ")}>
                  <div className={screen.trackPoint}></div>

                  <p>Shipped</p>
                  <p>Sep 25, 2025</p>
                </div>

                <div className={[screen.orderTrackerStep, deliveryStatusCode === 2 ? screen.halfCompleted : deliveryStatusCode > 2 && (screen.completed)].join(" ")}>
                  <div className={screen.trackPoint}></div>

                  <p>Out for delivery</p>
                  <p>Sep 27, 2025</p>
                </div>

                <div className={[screen.orderTrackerStep, deliveryStatusCode === 3 ? screen.lastCompleted : null].join(" ")}>
                  <div className={screen.trackPoint}></div>

                  <p>Delivered</p>
                  <p>Sep 27, 2025</p>
                </div>
              </div>
            </div>

            <div className={screen.overview}>
              <p className={screen.title}>Order details</p>
              <br />

              <div className={screen.listView}>
                <p>Size</p>
                <p>{values.productSize}</p>
              </div>

              <div className={screen.listView}>
                <p>Quantity</p>
                <p>{values.productQuantity}</p>
              </div>

              <div className={screen.listView}>
                <p>Discount</p>
                <p className={general.colorPrimary}>₹{values.productDiscount}</p>
              </div>

              {values.productCouponDiscount > 0 && (
                <div className={screen.listView}>
                  <p>Coupon</p>
                  <p className={general.colorPrimary}>₹{values.productCouponDiscount}</p>
                </div>
              )}

              <div className={screen.listView}>
                <p>Ordered on</p>
                <p>{values.productOrderedOn}</p>
              </div>

              <div className={screen.listView}>
                <p>Payment Method</p>
                <p>{values.productPaymentMethod === 'cod' ? 'Cash on delivery' : 'Online'}</p>
              </div>

              <div className={screen.listView}>
                <p>Order ID</p>
                <p>{values.productOrderID}</p>
              </div>

              <div className={screen.listView}>
                <p>Total amount</p>
                <p className={screen.subTitle}>₹{values.totalOrderAmount}</p>
              </div>

              {deliveryStatusCode <= 0 && (
                <>
                  <br></br>
                  <Link
                    className={[general.primaryBtn, general.w100, general.mgT25px, general.rounded1x].join(" ")} to={"/refund"}>
                    Cancel & Refund
                  </Link>
                  <p className={[general.textAlignCenter, general.fontSize13px, general.mgT10px].join(" ")}>Note: Cancellation is unavailable after your order has been shipped.</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyOrderDetails;